import { useState, useEffect } from 'react';
import { maintenanceState } from '@/lib/maintenance';
import { API_BASE } from '@/lib/api';

const POLL_INTERVAL_MS = 30_000;

interface MaintenanceStatusResponse {
  enabled: boolean;
  message: string | null;
  enabledAt: string | null;
}

async function fetchStatus(signal: AbortSignal): Promise<MaintenanceStatusResponse | null> {
  try {
    const response = await fetch(`${API_BASE}/maintenance/status`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) return null;
    return (await response.json()) as MaintenanceStatusResponse;
  } catch {
    return null;
  }
}

export function useMaintenanceMode() {
  const [state, setState] = useState(maintenanceState.getState);

  useEffect(() => {
    return maintenanceState.subscribe((isActive, message) => {
      setState({ isActive, message });
    });
  }, []);

  // Polls the public status endpoint so the SPA reflects maintenance mode
  // independently of whether the user has hit a 503 yet — and so it picks
  // up the OFF transition without requiring a page reload.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const sync = async () => {
      const status = await fetchStatus(controller.signal);
      if (cancelled || !status) return;
      const current = maintenanceState.getState();
      if (status.enabled && !current.isActive) {
        maintenanceState.setActive(status.message);
      } else if (!status.enabled && current.isActive) {
        maintenanceState.clearActive();
      }
    };

    sync();
    const interval = setInterval(sync, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
    };
  }, []);

  return state;
}
